import { React, useEffect, useRef, useState } from '../../shared/react.js';
import { Icon } from '../../shared/icons.js';
import { useAppRuntime, useAppState } from '../../app/app.context.js';
import type { MapView } from '../../app/app.types.js';
import { QueryFilterMenu } from '../query/QueryFilterMenu.js';

const viewOptions: readonly { readonly id: MapView; readonly label: string; readonly description: string }[] = [
  { id: 'space', label: 'Semantic space', description: 'Browse every artwork in its neighborhood.' },
  { id: 'focus', label: 'Result focus', description: 'Pull the active neighbors around the query.' },
  { id: 'wall', label: 'Collection wall', description: 'Browse the active image-backed collection.' }
];

export function MapToolbar() {
  const runtime = useAppRuntime();
  const state = useAppState();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filterRef = useRef(null as HTMLButtonElement | null);
  const viewRef = useRef(null as HTMLButtonElement | null);
  const [viewsOpen, setViewsOpen] = useState(false);
  const currentView = viewOptions.find((option) => option.id === state.mapView) ?? viewOptions[0]!;
  const filterCount = state.queryFilters.length + (state.scopedCollectionId ? 1 : 0);

  useEffect(() => {
    if (!viewsOpen) return;
    const anchor = viewRef.current as HTMLButtonElement | null;
    const container = anchor?.parentElement;
    if (!anchor || !container) return;
    const options = () => [...container.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]')];
    const close = () => { setViewsOpen(false); anchor.focus({ preventScroll: true }); };
    const keyboard = (event: KeyboardEvent) => {
      if (!container.contains(event.target as Node)) return;
      if (event.key === 'Escape') { event.preventDefault(); close(); }
      else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const items = options();
        const current = items.indexOf(document.activeElement as HTMLButtonElement);
        const next = current < 0 ? (event.key === 'ArrowDown' ? 0 : items.length - 1)
          : (current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
        items[next]?.focus();
      }
    };
    const outside = (event: PointerEvent) => {
      if (!container.contains(event.target as Node)) { event.preventDefault(); close(); }
    };
    const focusOutside = (event: FocusEvent) => {
      if (!container.contains(event.target as Node)) setViewsOpen(false);
    };
    document.addEventListener('focusin', focusOutside);
    document.addEventListener('keydown', keyboard);
    document.addEventListener('pointerdown', outside, true);
    return () => {
      document.removeEventListener('focusin', focusOutside);
      document.removeEventListener('keydown', keyboard);
      document.removeEventListener('pointerdown', outside, true);
    };
  }, [viewsOpen]);

  return (
    <header className="map-toolbar">
      <div className="map-toolbar__left">
        <div className="view-anchor">
          <button
            type="button"
            ref={viewRef}
            className="toolbar-button toolbar-button--view"
            aria-expanded={viewsOpen}
            aria-haspopup="menu"
            onClick={() => setViewsOpen((value: boolean) => !value)}
          >
            <span>{currentView.label}</span><Icon name="chevron-down" size={16}/>
          </button>
          {viewsOpen && (
            <div className="view-popover" role="menu" aria-label="Map view">
              {viewOptions.map((option) => (
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={state.mapView === option.id}
                  className={state.mapView === option.id ? 'is-selected' : ''}
                  key={option.id}
                  onClick={() => {
                    runtime.setMapView(option.id);
                    setViewsOpen(false);
                    viewRef.current?.focus({ preventScroll: true });
                  }}
                >
                  <span>{option.label}</span>
                  <small>{option.description}</small>
                </button>
              ))}
            </div>
          )}
        </div>
        <span className="toolbar-help" title="The map reorganizes continuously as query ingredients and filters change."><Icon name="help" size={20}/></span>
      </div>
      <div className="map-toolbar__right">
        <div className="filter-anchor">
          <button
            type="button"
            className={`toolbar-button${filterCount ? ' is-active' : ''}`}
            ref={filterRef}
            aria-haspopup="dialog"
            aria-expanded={filtersOpen}
            onClick={() => setFiltersOpen((value: boolean) => !value)}
          >
            <Icon name="filter" size={18}/><span>Filters</span>{filterCount > 0 && <b className="toolbar-count">{filterCount}</b>}
          </button>
          {filtersOpen && <QueryFilterMenu anchor={filterRef.current} onClose={() => setFiltersOpen(false)}/>}
        </div>
      </div>
    </header>
  );
}
